import type { SpinnerVariants } from "@v1/components/common/designSystem/components/spinner/Spinner";
import type { RelativeRoutingType, To } from "react-router-dom";
import type { ButtonSize, ButtonVariant } from "./button-shared";

import React from "react";

import { Spinner } from "@v1/components/common/designSystem/components/spinner/Spinner";
import { Tooltip } from "@v1/components/common/designSystem/components/tooltip/Tooltip";
import twMerge from "@v1/components/common/designSystem/utils/twMerge";
import { Link } from "react-router-dom";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

type TertiaryButtonProps = {
  variant: "tertiary";
  hasHoverHighlight?: boolean;
};

type OtherButtonProps = {
  variant: Exclude<ButtonVariant, "tertiary">;
};

type ButtonVariantProps = TertiaryButtonProps | OtherButtonProps;

type ButtonPropsBase = React.PropsWithChildren<
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "className"> & {
    size?: ButtonSize;
    isLoading?: boolean;
    layoutClassName?: string;
    UNSAFE_className?: string;
    id?: string;
    // Patrick (2025-01-23): Added this React.ReactElement | false here to accept react elements only (so we can clone the className), but also allow
    // syntax like arrowRight && <BsArrowRight />, so that we don't have to explicitly pass through undefined
    startEnhancer?: React.JSX.Element | false;
    endEnhancer?: React.JSX.Element | false;
    tooltipText?: React.ReactNode;
    to?: To;
    target?: string;
    rel?: string;
    replace?: boolean;
    state?: any;
    preventScrollReset?: boolean;
    relative?: RelativeRoutingType;
    spinnerSize?: number;
    hasMinDimensions?: boolean;
    UNSAFE_contentClassName?: string;
    layoutChildContainerClassName?: string;
    isActive?: boolean;
    titleAlignment?: "start" | "center";
    tooltipSide?: "top" | "bottom" | "left" | "right";
  }
>;
export type ButtonProps = ButtonPropsBase & ButtonVariantProps;

const TOOLTIP_SIDE_OFFSET = 8;

const Button = React.forwardRef<
  Omit<HTMLButtonElement | HTMLAnchorElement, "className">,
  ButtonProps
>((_props, ref) => {
  const {
    hasMinDimensions = true,
    children,
    variant,
    size = "sm",
    isLoading = false,
    startEnhancer,
    layoutChildContainerClassName,
    endEnhancer,
    tooltipText,
    to,
    target,
    rel,
    UNSAFE_contentClassName,
    replace,
    state,
    preventScrollReset,
    relative,
    spinnerSize,
    disabled,
    layoutClassName,
    UNSAFE_className,
    onClick,
    isActive,
    titleAlignment = "center",
    tooltipSide = "top",
    ...props
  } = _props;
  const hasHoverHighlight = exhaustiveSwitch(
    _props,
    "variant",
  )({
    tertiary: (props) => props.hasHoverHighlight,
    danger: () => false,
    primary: () => false,
    secondary: () => false,
    dangerLink: () => false,
    link: () => false,
    inherit: () => false,
  });

  const baseClass = twMerge(
    "group flex shrink-0 items-center justify-center cursor-pointer transition duration-300",
    "rounded-md font-sans whitespace-nowrap",

    // Size related styles
    size === "sm" && "py-1 px-2 h-6 text-xs font-medium",
    size === "base" &&
      "py-1.5 px-[8.5px] h-8 text-sm font-medium max-sm:py-2 max-sm:px-[8.5px] max-sm:h-10 max-sm:text-base",
    size === "lg" && "py-2 px-[8.5px] h-10 text-base font-medium",

    // Loading styles
    isLoading && variant === "tertiary" && "bg-surface",
    isLoading && variant === "link" && "bg-sky-soft",
    isLoading && variant === "dangerLink" && "bg-rose-soft",

    // Min Dimension styles
    hasMinDimensions &&
      variant !== "tertiary" &&
      variant !== "link" &&
      variant !== "dangerLink" &&
      size === "sm" &&
      "min-w-[64px]",
    hasMinDimensions &&
      variant !== "tertiary" &&
      variant !== "link" &&
      variant !== "dangerLink" &&
      size === "base" &&
      "min-w-[80px]",
    hasMinDimensions &&
      variant !== "tertiary" &&
      variant !== "link" &&
      variant !== "dangerLink" &&
      size === "lg" &&
      "min-w-[96px]",
    // Variant styles
    variantClassNames[variant],

    // isActive styles
    isActive && isActiveVariantMap[variant],
    disabled && "opacity-50 cursor-not-allowed",

    // Icon button stylesx
    hasHoverHighlight && !disabled && "hover:bg-surface",
    isActive && hasHoverHighlight && !disabled && "bg-surface",

    // Special styles
    layoutClassName,
    UNSAFE_className,
  );

  const baseEnhancerClass = twMerge(
    size === "sm" && "text-xs font-medium",
    size === "base" && "text-sm font-medium max-sm:text-base",
    size === "lg" && "text-base font-medium",
  );

  const content = (
    <div
      className={twMerge("flex flex-1 items-center", UNSAFE_contentClassName)}
    >
      {/* Patrick (2025-01-23):
          We're cloning the startEnhancer here in order to pass the baseEnhancerClass to it as a default.
          We force the enhancer to be a reactElement.

          If this gets inefficient in the future, we can memoize the startEnhancer
        */}
      {startEnhancer &&
        React.cloneElement(startEnhancer, {
          className: twMerge(baseEnhancerClass, startEnhancer.props.className),
        })}
      <div
        className={twMerge(
          "relative flex flex-1 items-center justify-center",
          layoutChildContainerClassName,
        )}
      >
        {isLoading && (
          <Spinner
            className="absolute"
            style={
              spinnerSize
                ? { width: spinnerSize, height: spinnerSize }
                : undefined
            }
            variant={spinnerVariantMap[variant]}
          />
        )}
        {children && (
          <div
            className={twMerge(
              "flex flex-1 w-full",
              titleAlignment === "start" && "justify-start",
              titleAlignment === "center" && "justify-center",
              isLoading && "invisible",
              startEnhancer && "ml-1",
              endEnhancer && "mr-1",
            )}
          >
            {children}
          </div>
        )}
      </div>
      {/* Patrick (2025-01-23):
          We're cloning the startEnhancer here in order to pass the baseEnhancerClass to it as a default.
          We force the enhancer to be a reactElement.

          If this gets inefficient in the future, we can memoize the endEnhancer
        */}
      {endEnhancer &&
        React.cloneElement(endEnhancer, {
          className: twMerge(baseEnhancerClass, endEnhancer.props.className),
        })}
    </div>
  );

  const sharedProps = {
    className: baseClass,
    style: props.style,
    disabled,
    "aria-disabled": disabled || undefined,
    onClick,
  };

  if (to) {
    const linkProps = {
      to,
      target,
      rel: rel || (target === "_blank" ? "noreferrer" : undefined),
      replace,
      state,
      preventScrollReset,
      relative,
      // Patrick (12-19-2024): The reason we have this onClick here is because we can't disable a <Link> so if it's disabled we need the onClick to not work
      onClick: (event: any) => {
        if (disabled) {
          event.preventDefault();
        } else if (onClick) {
          onClick(event);
        }
      },
    };
    return (
      <Tooltip
        content={tooltipText}
        sideOffset={TOOLTIP_SIDE_OFFSET}
        side={tooltipSide ?? "top"}
        triggerAsChild
      >
        <Link
          {...sharedProps}
          {...linkProps}
          ref={ref as React.Ref<HTMLAnchorElement>}
        >
          {content}
        </Link>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      content={tooltipText}
      sideOffset={TOOLTIP_SIDE_OFFSET}
      side={tooltipSide ?? "top"}
      triggerAsChild
    >
      <button
        {...props}
        // NOTE (Craig, 2025-04-04): sharedProps needs to be spread after props because props includes
        // className from trigger as child. If we spread props first, then we will override our button styles
        // and have default styles from radix-ui trigger.
        {...sharedProps}
        onClick={onClick as React.MouseEventHandler<HTMLButtonElement>}
        type={props.type || "button"}
        ref={ref as React.Ref<HTMLButtonElement>}
      >
        {content}
      </button>
    </Tooltip>
  );
});

const variantClassNames: Record<ButtonVariant, string> = {
  primary: "bg-default hover:bg-button-primary-hover text-white shadow-2xs",
  secondary:
    "bg-white hover:bg-button-secondary-hover border-0.5 border-component-border shadow-2xs",
  // Craig (2025-02-27): Removes highlight, padding, and dimensions from the button and adds underline
  // based on designs
  tertiary:
    "bg-transparent hover:underline p-0 h-[unset] w-[unset] max-sm:p-0 max-sm:h-[unset] max-sm:w-[unset]",
  // Craig (2025-02-27): Removes highlight, padding, and dimensions from the button and adds underline
  // based on designs
  link: "bg-transparent text-primary hover:underline p-0 h-[unset] w-[unset] max-sm:p-0 max-sm:h-[unset] max-sm:w-[unset]",
  danger: "bg-rose hover:bg-button-danger-hover text-white shadow-2xs",
  // Craig (2025-02-27): Removes highlight, padding, and dimensions from the button and adds underline
  // based on designs
  dangerLink:
    "bg-transparent hover:underline text-rose p-0 h-[unset] w-[unset] max-sm:p-0 max-sm:h-[unset] max-sm:w-[unset]",
  inherit: "bg-inherit text-inherit m-0 p-0",
};

const isActiveVariantMap: Record<ButtonVariant, string> = {
  primary: "bg-muted",
  secondary: "bg-neutral-soft",
  link: "underline",
  danger: "bg-button-danger-hover",
  tertiary: "underline",
  dangerLink: "underline",
  inherit: "",
};

const spinnerVariantMap: Record<ButtonVariant, SpinnerVariants> = {
  primary: "white",
  secondary: "default",
  tertiary: "default",
  danger: "rose",
  link: "primary",
  dangerLink: "rose",
  inherit: "primary",
};

Button.displayName = "Button";

/** @deprecated Use Button from @v1/components/common/designSystem/components/button/Button instead */
const DeprecatedButton = Button;

export default DeprecatedButton;
